/**
 * NotchesBoringBucketPage — /n2/boring-bucket
 *
 * Public introduction to The Boring Bucket mentored membership. Frames
 * BB as a mentored path through Understand / Evaluate / Execute.
 * Reachable from the landing entrance, the Framework page, and the
 * public plans comparison.
 *
 * The public application flow lives at theboringbucket.com (external).
 * The internal /boring-bucket/join route is the post-acceptance setup
 * page, which requires a signed invitation token — do NOT link an
 * anonymous visitor there.
 * This page is content-only — no payment initiation, no order entry.
 *
 * Pricing: $299/mo shown publicly. Boring Bucket REPLACES a customer's
 * research membership; it is not an additive $299. A customer upgrading
 * from All Research pays $299/mo total, not $498.99. The annual SKU
 * remains configured in productConfig.js at $2,990/yr but is not
 * promoted here pending owner confirmation.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { palette, type as typeTokens } from './theme';
import {
  NotchesExplainerFrame,
  useIsMobile,
  useReducedMotion,
} from './NotchesExplainerFrame';

type Benefit = {
  eyebrow: string;
  headline: string;
  body: string;
};

const BENEFITS: Benefit[] = [
  {
    eyebrow: 'Understand the reasoning',
    headline:
      'Explore market context and decisions through collective mentoring sessions and recordings.',
    body: 'Members join live mentoring sessions and review the recordings on their own time. The focus is on how market context translates into decisions, not on tips or calls.',
  },
  {
    eyebrow: 'Evaluate the approach',
    headline:
      'Study researched opportunities alongside the capital allocation framework.',
    body: 'Members see how buying-power allocation, position sizing, and profit handling connect to the research they read on Notches — the mechanics of a repeatable approach members can apply on their own accounts.',
  },
  {
    eyebrow: 'See it applied',
    headline:
      "Examine the founder's real-money positions and recent closes as examples of the approach in practice.",
    body: 'The founder\'s live-account view is provided as a study aid — a way to see the framework in practice. It is not a signal to copy, and Boring Bucket is not a managed account or advisory service.',
  },
];

type Component = {
  title: string;
  body: string;
};

const MEMBERSHIP_COMPONENTS: Component[] = [
  {
    title: "Founder's live-account view",
    body: 'A read-only window into the founder\'s real-money positions and recent closes. Presented as study material for the framework, not as instructions to copy.',
  },
  {
    title: 'Group mentoring',
    body: 'Group sessions where the founder walks through the framework in the current market — how the research is being read, what is being weighed, where the risks are.',
  },
  {
    title: 'Session recordings',
    body: 'Recordings of past sessions so members can review them on their own time.',
  },
  {
    title: 'Capital allocation framework',
    body: 'The plan the founder uses for buying-power allocation, position sizing, and profit handling. Explained so members can apply it to their own accounts and constraints.',
  },
  {
    title: 'Member community',
    body: 'A private space for members to discuss the framework, ask questions, and share their own applications.',
  },
];

export const NotchesBoringBucketPage: React.FC = () => {
  const isMobile = useIsMobile();
  const reduced = useReducedMotion();

  return (
    <NotchesExplainerFrame
      breadcrumbTrail={[
        { label: 'Notches', to: '/n2' },
        { label: 'The Boring Bucket' },
      ]}
    >
      <Hero reduced={reduced} isMobile={isMobile} />

      <Section title="Three ways members work with the framework">
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          {BENEFITS.map((b) => (
            <BenefitCard key={b.eyebrow} benefit={b} />
          ))}
        </ul>
      </Section>

      <Section title="What membership includes">
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            borderTop: `1px solid ${palette.line}`,
          }}
        >
          {MEMBERSHIP_COMPONENTS.map((c) => (
            <ComponentRow key={c.title} component={c} />
          ))}
        </ul>
      </Section>

      <Section title="Membership">
        <div
          style={{
            border: `1px solid ${palette.gold}`,
            background: palette.paper2,
            borderRadius: 2,
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <h3
              style={{
                fontFamily: typeTokens.serif,
                fontSize: 24,
                fontWeight: 400,
                margin: 0,
                color: palette.ink,
                lineHeight: 1.2,
              }}
            >
              The Boring Bucket — Mentored
            </h3>
            <div
              style={{
                fontFamily: typeTokens.sans,
                fontSize: 14,
                color: palette.ink2,
                textAlign: 'right',
              }}
            >
              <div style={{ color: palette.ink }}>
                <strong>$299</strong> / month
              </div>
            </div>
          </div>
          <p style={{ ...paragraphStyle, margin: 0 }}>
            Includes everything in <strong>All Research</strong> — Premier
            plus SPX, SPY, QQQ, IWM, and ES, MES, GC, CL research — plus
            the mentored membership components above.
          </p>
          <p
            style={{
              fontFamily: typeTokens.sans,
              fontSize: 13,
              color: palette.ink2,
              lineHeight: 1.55,
              margin: 0,
              paddingTop: 8,
              borderTop: `1px solid ${palette.line}`,
            }}
          >
            <strong>Boring Bucket replaces your research membership.</strong>{' '}
            If you're already on Premier or a research plan, Boring Bucket
            is not an additional charge — you pay $299/month total, not the
            sum of both.
          </p>
          <p
            style={{
              fontFamily: typeTokens.sans,
              fontSize: 13,
              color: palette.ink3,
              lineHeight: 1.55,
              margin: 0,
              fontStyle: 'italic',
            }}
          >
            Includes the coverage listed. New markets and products are not
            automatically included. Automated engine subscriptions are
            separate.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 6 }}>
            <CtaLink href="https://theboringbucket.com" primary>
              Apply at theboringbucket.com →
            </CtaLink>
            <CtaLink to="/n2/plans">Compare all plans →</CtaLink>
          </div>
        </div>
      </Section>

      <Section title="Important boundaries">
        <ul style={notListStyle}>
          <li>
            The founder's account view is study material — an opportunity
            to see the framework in practice — not instructions to copy or
            personalized investment advice.
          </li>
          <li>
            Members trade on their own accounts through their own brokers.
            Boring Bucket is not a managed account or advisory service.
          </li>
          <li>
            Automated trading engines are separate subscriptions with their
            own configuration; membership does not include automation.
          </li>
        </ul>
      </Section>

      <Section title="Where to next">
        <div style={ctaRowStyle(isMobile)}>
          <CtaLink href="https://theboringbucket.com" primary>
            Apply at theboringbucket.com →
          </CtaLink>
          <CtaLink to="/n2/framework">How the Framework works →</CtaLink>
          <CtaLink to="/n2/plans">Compare all plans →</CtaLink>
          <CtaLink to="/n2">← Back to Notches</CtaLink>
        </div>
      </Section>
    </NotchesExplainerFrame>
  );
};

// ─── Building blocks ─────────────────────────────────────────────────────

const Hero: React.FC<{ reduced: boolean; isMobile: boolean }> = ({
  reduced,
  isMobile,
}) => (
  <header
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      animation: reduced
        ? undefined
        : 'notches-explainer-fade-in 320ms ease-out both',
    }}
  >
    <span
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 12,
        fontWeight: 700,
        color: palette.ink3,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
      }}
    >
      The Boring Bucket
    </span>
    <h1
      tabIndex={-1}
      style={{
        fontFamily: typeTokens.serif,
        fontSize: isMobile ? 32 : 42,
        fontWeight: 400,
        margin: 0,
        color: palette.ink,
        letterSpacing: '-0.01em',
        lineHeight: 1.15,
        textWrap: 'balance' as any,
        outline: 'none',
      }}
    >
      The Notches Framework,{' '}
      <em style={{ fontStyle: 'italic', color: palette.goldDeep, fontWeight: 500 }}>
        with guidance.
      </em>
    </h1>
    <p
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 16,
        color: palette.ink2,
        lineHeight: 1.55,
        margin: 0,
        maxWidth: 620,
      }}
    >
      Learn how the founder applies the framework through a live-account
      view, collective mentoring, a capital allocation plan, and member
      discussions. A mentored path through Understand, Evaluate, and
      Execute.
    </p>
  </header>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <h2
      style={{
        fontFamily: typeTokens.serif,
        fontSize: 24,
        fontWeight: 400,
        margin: 0,
        color: palette.ink,
        letterSpacing: '-0.005em',
        lineHeight: 1.2,
      }}
    >
      {title}
    </h2>
    {children}
  </section>
);

const BenefitCard: React.FC<{ benefit: Benefit }> = ({ benefit }) => (
  <li
    style={{
      border: `1px solid ${palette.line}`,
      borderRadius: 2,
      padding: 20,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      background: palette.paper,
    }}
  >
    <span
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 12,
        fontWeight: 700,
        color: palette.goldDeep,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
      }}
    >
      {benefit.eyebrow}
    </span>
    <h3
      style={{
        fontFamily: typeTokens.serif,
        fontSize: 20,
        fontWeight: 400,
        margin: 0,
        color: palette.ink,
        lineHeight: 1.25,
        textWrap: 'balance' as any,
      }}
    >
      {benefit.headline}
    </h3>
    <p style={{ ...paragraphStyle, margin: 0 }}>{benefit.body}</p>
  </li>
);

const ComponentRow: React.FC<{ component: Component }> = ({ component }) => (
  <li
    style={{
      borderBottom: `1px solid ${palette.line}`,
      padding: '18px 0',
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    }}
  >
    <span
      style={{
        fontFamily: typeTokens.serif,
        fontSize: 18,
        fontWeight: 400,
        color: palette.ink,
        lineHeight: 1.25,
      }}
    >
      {component.title}
    </span>
    <p style={{ ...paragraphStyle, margin: 0, fontSize: 14 }}>
      {component.body}
    </p>
  </li>
);

const CtaLink: React.FC<{
  to?: string;
  href?: string;
  primary?: boolean;
  children: React.ReactNode;
}> = ({ to, href, primary, children }) => {
  const style: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    borderRadius: 2,
    textDecoration: 'none',
    fontFamily: typeTokens.sans,
    fontSize: 14,
    fontWeight: 600,
    color: primary ? palette.paper : palette.ink,
    background: primary ? palette.goldDeep : 'transparent',
    border: `1px solid ${primary ? palette.goldDeep : palette.line}`,
    transition: 'background 150ms ease-out, color 150ms ease-out',
  };
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={style}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to || '#'} style={style}>
      {children}
    </Link>
  );
};

const paragraphStyle: React.CSSProperties = {
  fontFamily: typeTokens.sans,
  fontSize: 15,
  color: palette.ink2,
  lineHeight: 1.6,
  margin: 0,
  maxWidth: 640,
};

const notListStyle: React.CSSProperties = {
  fontFamily: typeTokens.sans,
  fontSize: 15,
  color: palette.ink2,
  lineHeight: 1.6,
  margin: 0,
  paddingLeft: 22,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  maxWidth: 640,
};

const ctaRowStyle = (isMobile: boolean): React.CSSProperties => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 12,
  flexWrap: 'wrap',
});
